import { NumberInput, Textarea, TextInput } from '@mantine/core';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { TractInputSchema } from '../schemas';

/**
 * Tract edit-form field registry. Mirror of api/src/lib/tract-form-layout-schema.ts.
 *
 * Context bag is empty — every tract field is self-contained text/number.
 */

export const TRACT_FORM_FIELD_NAMES = [
  'name',
  'state',
  'county',
  'description',
  'township',
  'range',
  'section',
  'block',
  'lot',
  'abstract',
  'survey',
  'apn',
  'gross_acres',
  'notes',
] as const;

export type TractFormFieldName = (typeof TRACT_FORM_FIELD_NAMES)[number];

export type TractFormContext = Record<string, never>;

type TractFieldDescriptor = FieldDescriptor<TractInputSchema, TractFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): TractFieldDescriptor {
  const desc: TractFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): TractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function acresField(label: string, defaultSpan: number): TractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <NumberInput
            label={descriptor.label}
            decimalScale={4}
            min={0}
            value={(field.value as number | null) ?? ''}
            onChange={(v) => field.onChange(typeof v === 'number' ? v : null)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

export const TRACT_FORM_FIELDS: FieldRegistry<
  TractInputSchema,
  TractFormFieldName,
  TractFormContext
> = {
  name: textField('Name', 12, { required: true }),
  state: textField('State', 6, { placeholder: 'TX', required: true }),
  county: textField('County', 6, { required: true }),
  description: textareaField('Legal description', 12),
  township: textField('Township', 4),
  range: textField('Range', 4),
  section: textField('Section', 4),
  block: textField('Block', 6),
  lot: textField('Lot', 6),
  abstract: textField('Abstract (TX)', 4),
  survey: textField('Survey (TX)', 4),
  apn: textField('APN', 4, { placeholder: "Assessor's parcel number" }),
  gross_acres: acresField('Gross acres', 12),
  notes: textareaField('Notes', 12),
};

/** Built-in default. Mirrors api/src/lib/tract-form-layout-schema.ts. */
export const TRACT_DEFAULT_LAYOUT: LayoutDocument<TractFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'basics',
      title: 'Basics',
      fields: [
        { name: 'name', span: 12 },
        { name: 'state', span: 6 },
        { name: 'county', span: 6 },
      ],
    },
    {
      id: 'description',
      title: 'Legal description',
      fields: [{ name: 'description', span: 12 }],
    },
    {
      id: 'legal_anchor',
      title: 'Legal anchor',
      fields: [
        { name: 'township', span: 4 },
        { name: 'range', span: 4 },
        { name: 'section', span: 4 },
        { name: 'block', span: 6 },
        { name: 'lot', span: 6 },
        { name: 'abstract', span: 4 },
        { name: 'survey', span: 4 },
        { name: 'apn', span: 4 },
      ],
    },
    {
      id: 'acreage',
      title: 'Acreage',
      fields: [{ name: 'gross_acres', span: 12 }],
    },
    {
      id: 'notes',
      title: 'Notes',
      fields: [{ name: 'notes', span: 12 }],
    },
  ],
};
